import React,{useState,useEffect} from 'react';
import {useAppContext} from '../context/AppContext';
import {getOrCreateAffiliate,COMMISSION_RATE_PCT,requestPayout} from '../utils/affiliate';
import * as FiIcons from 'react-icons/fi';
const {FiArrowLeft,FiCopy,FiCheck,FiDollarSign,FiUsers,FiTrendingUp,FiZap,FiLink,FiShare2,FiAward,FiInfo,FiExternalLink,FiSend}=FiIcons;

function StatCard({icon: Icon,label,value,color='#ff00cc',sub}) {
  return (
    <div className="rounded-xl p-4 text-center" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.07)'}}>
      <Icon className="mx-auto mb-1.5 text-xl" style={{color}} />
      <div className="font-black" style={{color,fontSize:'26px'}}>{value}</div>
      <div className="text-gray-500 uppercase tracking-wider mt-0.5" style={{fontSize:'11px'}}>{label}</div>
      {sub && <div className="text-gray-600 mt-0.5" style={{fontSize:'11px'}}>{sub}</div>}
    </div>
  );
}

function HowItWorksStep({num,title,desc}) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-9 h-9 rounded-full flex items-center justify-center font-black shrink-0 text-sm" style={{background:'linear-gradient(135deg,#ff00cc,#cc00ff)',color:'#fff'}}>{num}</div>
      <div>
        <div className="font-black text-white text-sm mb-0.5">{title}</div>
        <div className="text-gray-500 text-xs leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}

function PayoutForm({affiliate,onSubmitted}) {
  const [method,setMethod]=useState('paypal');
  const [address,setAddress]=useState('');
  const [notes,setNotes]=useState('');
  const [loading,setLoading]=useState(false);
  const [done,setDone]=useState(false);
  const [error,setError]=useState('');
  const pending=+(affiliate.total_earned-(affiliate.paid_out||0)).toFixed(2);

  const handleSubmit=async (e)=> {
    e.preventDefault();
    if (!address.trim()) {setError('Please enter your payment address.');return;}
    setLoading(true);setError('');
    try {
      await requestPayout({affiliateId:affiliate.id,code:affiliate.code,email:affiliate.email,method,address,amount:pending,notes});
      setDone(true);
      onSubmitted();
    } catch(err) {
      setError(err.message||'Failed to submit request. Please try again.');
    }
    setLoading(false);
  };

  if (done) return (
    <div className="flex items-center gap-3 p-4 rounded-xl" style={{background:'rgba(0,255,136,0.07)',border:'1px solid rgba(0,255,136,0.3)'}}>
      <FiCheck className="text-green-400 text-xl shrink-0" />
      <div>
        <div className="font-black text-green-400 text-sm uppercase tracking-wider">Payout Request Submitted!</div>
        <div className="text-gray-400 text-xs mt-0.5">We'll process your <span className="text-green-400 font-black">${pending}</span> payout within 3–5 business days.</div>
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="rounded-2xl p-5 space-y-4" style={{background:'rgba(0,255,136,0.04)',border:'1px solid rgba(0,255,136,0.25)'}}>
      <div className="flex items-center gap-2">
        <FiDollarSign className="text-green-400" />
        <span className="font-black text-white uppercase tracking-wider text-sm">Request Payout — <span style={{color:'#00ff88'}}>${pending}</span></span>
      </div>

      {/* Method selector */}
      <div>
        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Payment Method</label>
        <div className="flex rounded-xl overflow-hidden border border-gray-800 text-xs">
          {[{id:'paypal',label:'💳 PayPal'},{id:'crypto',label:'₿ Crypto'}].map(m=> (
            <button key={m.id} type="button" onClick={()=> setMethod(m.id)} className="flex-1 py-2.5 font-black uppercase tracking-wider transition-all" style={method===m.id?{background:'rgba(0,255,136,0.15)',color:'#00ff88'}:{color:'#6b7280'}}>
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Address */}
      <div>
        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5">
          {method==='paypal' ? 'PayPal Email Address' : 'Crypto Wallet Address'}
        </label>
        <input
          type={method==='paypal'?'email':'text'}
          value={address}
          onChange={e=> setAddress(e.target.value)}
          placeholder={method==='paypal'?'yourname@email.com':'0x... or bc1q...'}
          className="w-full px-4 py-2.5 rounded-xl text-white text-sm focus:outline-none"
          style={{background:'rgba(255,255,255,0.05)',border:'1px solid rgba(0,255,136,0.3)'}}
          onFocus={e=> e.target.style.borderColor='rgba(0,255,136,0.6)'}
          onBlur={e=> e.target.style.borderColor='rgba(0,255,136,0.3)'}
        />
        {method==='crypto' && (
          <p className="text-gray-600 text-xs mt-1">We support BTC, ETH, USDC. Specify the coin in notes.</p>
        )}
      </div>

      {/* Notes */}
      <div>
        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5">Notes (optional)</label>
        <input
          type="text"
          value={notes}
          onChange={e=> setNotes(e.target.value)}
          placeholder={method==='crypto'?'e.g. USDC on Ethereum':'Any extra info...'}
          className="w-full px-4 py-2.5 rounded-xl text-white text-sm focus:outline-none"
          style={{background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)'}}
        />
      </div>

      {error && <p className="text-red-400 text-xs">{error}</p>}

      <button type="submit" disabled={loading} className="w-full py-3 rounded-xl font-black text-sm uppercase tracking-widest text-white flex items-center justify-center gap-2 disabled:opacity-60 transition-all hover:scale-[1.02]" style={{background:'linear-gradient(135deg,#00ff88,#00cc6a)',color:'#000'}}>
        {loading ? <div className="w-4 h-4 rounded-full border-2 border-black/30 border-t-black animate-spin" /> : <><FiSend /> Submit Payout Request</>}
      </button>
    </form>
  );
}

export default function AffiliatePage() {
  const {user,setCurrentPage,setIsAuthModalOpen}=useAppContext();
  const [affiliate,setAffiliate]=useState(null);
  const [loading,setLoading]=useState(false);
  const [copied,setCopied]=useState(false);
  const [payoutSubmitted,setPayoutSubmitted]=useState(false);
  const baseUrl=window.location.origin+window.location.pathname;
  const refLink=affiliate ? `${baseUrl}?ref=${affiliate.code}` : '';

  useEffect(()=> {
    if (!user) return;
    setLoading(true);
    getOrCreateAffiliate(user).then(a=> {setAffiliate(a);setLoading(false);});
  },[user]);

  const copyLink=()=> {navigator.clipboard.writeText(refLink);setCopied(true);setTimeout(()=> setCopied(false),2000);};
  const shareTwitter=()=> {
    const text=encodeURIComponent(`🔥 Grab your piece of internet history on the 1 Million Pixel Grid!\n\nPrices rise every tier — lock in the lowest price now:\n${refLink}`);
    window.open(`https://twitter.com/intent/tweet?text=${text}`,'_blank','noopener');
  };

  const pendingPayout=affiliate ? +(affiliate.total_earned-(affiliate.paid_out||0)).toFixed(2) : 0;
  const PAYOUT_THRESHOLD=25;

  return (
    <div className="min-h-screen" style={{background:'#080012',fontFamily:'Courier New,monospace',color:'#fff'}}>
      <div className="sticky top-0 z-10 px-6 pt-6 pb-4 border-b border-pink-500/20" style={{background:'rgba(8,0,18,0.97)',backdropFilter:'blur(12px)'}}>
        <button onClick={()=> setCurrentPage('home')} className="flex items-center gap-2 text-pink-500 hover:text-pink-300 transition-colors font-bold uppercase tracking-widest mb-4" style={{fontSize:'13px'}}>
          <FiArrowLeft /> Back to Grid
        </button>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <FiDollarSign className="text-pink-500 text-sm" />
              <span className="text-pink-500 text-xs font-black uppercase tracking-widest">Earn Real Money</span>
            </div>
            <h1 className="font-black uppercase leading-none" style={{fontFamily:'Impact,sans-serif',letterSpacing:'2px'}}>
              <span className="text-white" style={{fontSize:'clamp(28px,5vw,52px)'}}>AFFILIATE </span>
              <span style={{color:'#ff00cc',fontSize:'clamp(28px,5vw,52px)'}}>PROGRAM</span>
            </h1>
            <p className="text-gray-400 text-sm mt-1">Earn <span className="text-pink-400 font-black">{COMMISSION_RATE_PCT}% commission</span> on every sale you refer.</p>
          </div>
        </div>
        <div className="mt-3 h-px w-full" style={{background:'linear-gradient(90deg,#ff00cc,transparent)'}} />
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 pb-24 space-y-8">
        {/* Hero */}
        <div className="rounded-2xl p-6 text-center relative overflow-hidden" style={{background:'linear-gradient(135deg,rgba(255,0,200,0.12),rgba(204,0,255,0.12))',border:'1px solid rgba(255,0,200,0.35)'}}>
          <div className="absolute inset-0 pointer-events-none" style={{background:'radial-gradient(ellipse at 50% 0%,rgba(255,0,200,0.15),transparent 70%)'}} />
          <div className="relative">
            <div className="text-6xl font-black mb-1" style={{color:'#ff00cc',fontFamily:'Impact,sans-serif'}}>{COMMISSION_RATE_PCT}%</div>
            <div className="text-white font-black uppercase tracking-widest text-lg mb-2">Commission on Every Sale</div>
            <p className="text-gray-400 text-sm max-w-sm mx-auto">Share your unique link. When someone buys pixels, you earn {COMMISSION_RATE_PCT}% of the sale — automatically tracked.</p>
            <div className="flex items-center justify-center gap-6 mt-4 flex-wrap">
              {[{label:'100 px @ $0.15',earn:'$0.30'},{label:'1,000 px @ $0.15',earn:'$3.00'},{label:'10,000 px @ $0.40',earn:'$80.00'}].map((ex,i)=> (
                <div key={i} className="text-center">
                  <div className="text-gray-500 text-xs">{ex.label}</div>
                  <div className="font-black text-green-400" style={{fontSize:'18px'}}>You earn {ex.earn}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Not signed in */}
        {!user && (
          <div className="text-center py-12 rounded-2xl" style={{border:'1px dashed rgba(255,0,200,0.25)',background:'rgba(255,0,200,0.03)'}}>
            <FiAward className="text-pink-500 text-4xl mx-auto mb-4" />
            <h2 className="font-black text-white text-xl uppercase mb-2" style={{fontFamily:'Impact,sans-serif'}}>Sign in to get your link</h2>
            <p className="text-gray-500 text-sm mb-6">Create a free account to get your unique referral link and start earning.</p>
            <button onClick={()=> setIsAuthModalOpen(true)} className="px-8 py-3.5 rounded-xl font-black text-sm uppercase tracking-widest text-white" style={{background:'linear-gradient(135deg,#ff00cc,#cc00ff)',boxShadow:'0 0 20px rgba(255,0,200,0.4)'}}>
              Sign In / Sign Up Free
            </button>
          </div>
        )}

        {user && loading && <div className="text-center py-12 text-gray-600">Loading your affiliate dashboard...</div>}

        {user && !loading && affiliate && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard icon={FiUsers} label="Clicks" value={affiliate.clicks||0} color="#00ffff" />
              <StatCard icon={FiZap} label="Conversions" value={affiliate.conversions||0} color="#ff00cc" />
              <StatCard icon={FiTrendingUp} label="Total Earned" value={`$${(affiliate.total_earned||0).toFixed(2)}`} color="#00ff88" />
              <StatCard icon={FiDollarSign} label="Pending Payout" value={`$${pendingPayout}`} color="#f59e0b" sub={`Min $${PAYOUT_THRESHOLD}`} />
            </div>

            {/* Referral link */}
            <div className="rounded-2xl p-5 space-y-4" style={{background:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,0,200,0.25)'}}>
              <div className="flex items-center gap-2">
                <FiLink className="text-pink-500" />
                <span className="font-black text-white uppercase tracking-wider text-sm">Your Referral Link</span>
                <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-black uppercase" style={{background:'rgba(0,255,136,0.15)',color:'#00ff88',border:'1px solid rgba(0,255,136,0.3)'}}>Code: {affiliate.code}</span>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-xl" style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)'}}>
                <span className="flex-1 text-gray-300 text-xs font-mono truncate">{refLink}</span>
                <button onClick={copyLink} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-black text-xs uppercase tracking-wider shrink-0 transition-all hover:scale-105" style={{background:copied?'rgba(0,255,136,0.2)':'rgba(255,0,200,0.15)',color:copied?'#00ff88':'#ff00cc',border:`1px solid ${copied?'rgba(0,255,136,0.4)':'rgba(255,0,200,0.4)'}`}}>
                  {copied ? <><FiCheck /> Copied!</> : <><FiCopy /> Copy</>}
                </button>
              </div>
              <div className="flex gap-3 flex-wrap">
                <button onClick={shareTwitter} className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider transition-all hover:scale-105" style={{background:'rgba(29,161,242,0.15)',border:'1px solid rgba(29,161,242,0.35)',color:'#1DA1F2'}}>
                  <FiShare2 /> Share on X (Twitter)
                </button>
                <button onClick={copyLink} className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider transition-all hover:scale-105" style={{background:'rgba(255,0,200,0.12)',border:'1px solid rgba(255,0,200,0.3)',color:'#ff00cc'}}>
                  <FiExternalLink /> Copy & Share
                </button>
              </div>
            </div>

            {/* Payout section */}
            {pendingPayout >= PAYOUT_THRESHOLD && !payoutSubmitted ? (
              <PayoutForm affiliate={affiliate} onSubmitted={()=> setPayoutSubmitted(true)} />
            ) : payoutSubmitted ? (
              <div className="flex items-center gap-3 p-4 rounded-xl" style={{background:'rgba(0,255,136,0.07)',border:'1px solid rgba(0,255,136,0.3)'}}>
                <FiCheck className="text-green-400 text-xl shrink-0" />
                <div>
                  <div className="font-black text-green-400 text-sm uppercase tracking-wider">Payout Request Submitted!</div>
                  <div className="text-gray-400 text-xs mt-0.5">We'll process your payout within 3–5 business days.</div>
                </div>
              </div>
            ) : pendingPayout > 0 ? (
              <div className="flex items-center gap-4 p-4 rounded-xl" style={{background:'rgba(245,158,11,0.06)',border:'1px solid rgba(245,158,11,0.25)'}}>
                <FiInfo className="text-yellow-400 shrink-0" />
                <div className="text-gray-400 text-xs">
                  Minimum payout is <span className="text-white font-black">${PAYOUT_THRESHOLD}</span>. You have <span className="text-yellow-400 font-black">${pendingPayout}</span> — need <span className="text-white font-black">${(PAYOUT_THRESHOLD-pendingPayout).toFixed(2)}</span> more. Once you hit $25 in earnings, a form will appear here for you to request a payout via PayPal or crypto transfer.
                </div>
              </div>
            ) : null}
          </>
        )}

        {/* How it works */}
        <div className="rounded-2xl p-6 space-y-5" style={{background:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.07)'}}>
          <h2 className="font-black text-white uppercase tracking-widest text-sm flex items-center gap-2"><FiZap className="text-pink-500" /> How It Works</h2>
          <div className="space-y-4">
            <HowItWorksStep num="1" title="Sign up for free" desc="Create an account — takes 10 seconds. You get a unique referral link instantly." />
            <HowItWorksStep num="2" title="Share your link" desc="Post it on Twitter, Reddit, your blog, Discord — anywhere. Your code is tracked automatically." />
            <HowItWorksStep num="3" title="Earn 20% commission" desc="Every time someone buys pixels through your link, you earn 20% of the sale. No cap." />
            <HowItWorksStep num="4" title="Get paid" desc="Once you hit $25 in earnings, a payout form appears right here. Request via PayPal or crypto transfer." />
          </div>
        </div>

        {/* Why promote */}
        <div className="rounded-2xl p-6" style={{background:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.07)'}}>
          <h2 className="font-black text-white uppercase tracking-widest text-sm mb-4 flex items-center gap-2"><FiAward className="text-pink-500" /> Why Promote This?</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              {emoji:'📈',title:'Prices keep rising',desc:"Every tier increase creates urgency — your audience is motivated to buy now."},
              {emoji:'💰',title:'20% is huge',desc:"Most affiliate programs pay 5-10%. We pay 20% because you deserve more."},
              {emoji:'🔗',title:'Easy sell',desc:"Pixel grids are viral. People love owning a piece of internet history."},
              {emoji:'♾️',title:'Passive income',desc:"Your link works 24/7. One post can earn commissions for months."},
            ].map((item,i)=> (
              <div key={i} className="flex items-start gap-3 p-3 rounded-xl" style={{background:'rgba(255,255,255,0.03)'}}>
                <span className="text-xl shrink-0">{item.emoji}</span>
                <div>
                  <div className="font-black text-white text-sm">{item.title}</div>
                  <div className="text-gray-500 text-xs mt-0.5">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {!user && (
          <div className="text-center">
            <button onClick={()=> setIsAuthModalOpen(true)} className="px-10 py-4 rounded-xl font-black text-lg uppercase tracking-widest text-white inline-flex items-center gap-3 transition-all hover:scale-105" style={{background:'linear-gradient(135deg,#ff00cc,#cc00ff)',boxShadow:'0 0 30px rgba(255,0,200,0.4)',fontFamily:'Impact,sans-serif'}}>
              <FiZap /> Start Earning Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}